import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    departments = "departments",
    heading = "heading",
    lastLogo = "lastLogo",
    lastTittle = "lastTittle",
}

interface MemberList {
    fields: {
        tittle: string;
        description: string;
        img: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.departments]: MemberList[];
        [fields.heading]: string;
        [fields.lastLogo]: string;
        [fields.lastTittle]: string;
    };
}

export interface SurCareersValuesStyles {
    tittle: InjectableCss;
    heading?: InjectableCss;
    description: InjectableCss;
    box?: InjectableCss;
    container?: InjectableCss;
    icon?: InjectableCss;
    headingContainer?: InjectableCss;
    mainContainer?: InjectableCss;
    mobileTittle?: InjectableCss;
    wrapper?: InjectableCss;
    mobileWrapper?: InjectableCss;
    mobileHeadingContainer?: InjectableCss;
    mobileDescription?: InjectableCss;
    mobileContainer?: InjectableCss;
    lastLogo?: InjectableCss;
    lowerContainer?: InjectableCss;
}

export const surCareersValuesStyles: SurCareersValuesStyles = {
    container: {
        css: css`
            display: flex;
            width: 100%;
            margin-bottom: 58px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    tittle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
            max-width: 156px;
            flex: 0 0 30%;
        `,
    },
    description: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("text.accent")};
            max-width: 393px;
            flex: 0 0 70%;
        `,
    },
    box: {
        css: css`
            display: flex;
            gap: 20px;
            align-items: center;
        `,
    },
    heading: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 38px;
            text-align: center;
            text-transform: uppercase;
            color: ${getColor("text.main")};
        `,
    },
    icon: {
        css: css`
            width: 100%;
            max-width: 24px;
            max-height: 25px;
            flex: 0 0 10%;
        `,
    },
    headingContainer: {
        css: css`
            margin-bottom: 58px;
        `,
    },
    mainContainer: {
        css: css`
            max-width: 611px;
            margin: 0 auto;
            /* padding: 0px 41px; */
        `,
    },
    mobileTittle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
        `,
    },
    wrapper: {
        css: css`
            padding: 86px 0px;
        `,
    },
    mobileWrapper: {
        css: css`
            padding: 20px;
        `,
    },
    mobileHeadingContainer: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
        `,
    },
    mobileDescription: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("text.accent")};
            margin-top: 10px;
        `,
    },
    mobileContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
        `,
    },
    lowerContainer: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 21px;
        `,
    },
};

const styles = surCareersValuesStyles;

export const SurCareersValues: FC<OwnProps> = ({ fields }) => {
    return (
        <>
            <Hidden below="lg">
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.headingContainer}>
                        <StyledWrapper {...styles.heading}>{fields.heading}</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainContainer}>
                        {fields.departments?.map(division => {
                            return (
                                <>
                                    <StyledWrapper {...styles.container}>
                                        <StyledWrapper {...styles.box}>
                                            <LazyImage
                                                src={division.fields.img}
                                                {...styles.icon}
                                                altText={division.fields.tittle || "Our Values"}
                                            />
                                            <StyledWrapper {...styles.tittle}>{division.fields.tittle}</StyledWrapper>
                                            <StyledWrapper {...styles.description}>
                                                {division.fields.description}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </>
                            );
                        })}
                    </StyledWrapper>
                    <StyledWrapper {...styles.lowerContainer}>
                        <LazyImage
                            src={fields.lastLogo}
                            {...styles.icon}
                            altText={fields.lastTittle || "main content"}
                        />
                        <StyledWrapper {...styles.tittle}>{fields.lastTittle}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.mobileWrapper}>
                    <StyledWrapper {...styles.mobileHeadingContainer}>
                        <StyledWrapper {...styles.heading}>{fields.heading}</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainContainer}>
                        {fields.departments?.map(division => {
                            return (
                                <>
                                    <StyledWrapper {...styles.mobileContainer}>
                                        <StyledWrapper {...styles.box}>
                                            <LazyImage
                                                src={division.fields.img}
                                                {...styles.icon}
                                                altText={division.fields.tittle || "Our Values"}
                                            />
                                            <StyledWrapper {...styles.mobileTittle}>
                                                {division.fields.tittle}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.mobileDescription}>
                                            {division.fields.description}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </>
                            );
                        })}
                    </StyledWrapper>
                    <StyledWrapper {...styles.lowerContainer}>
                        <LazyImage
                            src={fields.lastLogo}
                            {...styles.icon}
                            altText={fields.lastTittle || "main content"}
                        />
                        <StyledWrapper {...styles.tittle}>{fields.lastTittle}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SurCareersValues,
    definition: {
        group: "Basic",
        displayName: "SurCareersValues",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
            },
            {
                name: fields.departments,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.tittle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tittle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "img",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        displayName: "Image",
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.lastTittle,
                displayName: "Last Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
            },
            {
                fieldType: "Translatable",
                name: fields.lastLogo,
                displayName: "Last Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
        ],
    },
};
export default widgetModule;
